import {
  type SearchEndpointArgs,
  useInfiniteScroll,
  useLazyGetSearchResultsQuery,
} from '@carboncopyco/data';
import type { Playbook, PlaybookAsset, Sort } from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import constate from 'constate';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { AssetSelectionEntities, PlaybookSearchResult } from './types';

const playbookInitialName = 'Untitled Playbook';
const playbookInitialMultiTab = false;

const cleanAssetForComparison = (asset: PlaybookAsset) => ({
  ...asset,
  entity: {
    ...Object.fromEntries(
      Object.entries(asset.entity).filter(
        ([key]) =>
          key !== 'playerToken' &&
          key !== 'fileServiceToken' &&
          key !== 'totalShares' &&
          key !== 'image' &&
          key !== 'rules' &&
          key !== 'variables' &&
          key !== 'entityType' &&
          key !== 'totalPresentations'
      )
    ),
    creator: Object.fromEntries(
      Object.entries(asset.entity.creator).filter(([key]) => key !== 'image')
    ),
  },
});

type UsePlaybookModalProps = {
  workspaceId: string;
  playbook?: Playbook;
};

type PlaybookModalInitialState = {
  assets: PlaybookAsset[];
  playbookName: string;
  multiTab: boolean;
};
const usePlaybookModalContext = ({
  workspaceId,
  playbook,
}: UsePlaybookModalProps) => {
  const initialState = useMemo<PlaybookModalInitialState>(
    () =>
      playbook
        ? {
            assets: playbook.assets,
            playbookName: playbook.name,
            multiTab: !!playbook.playSettings?.multiTab,
          }
        : {
            assets: [],
            playbookName: playbookInitialName,
            multiTab: playbookInitialMultiTab,
          },
    [playbook]
  );
  const { organizationId } = useParams();
  const [isEditingExternalLink, setIsEditingExternalLink] = useBoolean();
  const [playbookNameError, setPlaybookNameError] = useBoolean();
  const [playbookName, setPlaybookName] = useState(
    playbook?.name || playbookInitialName
  );
  const [query, setQuery] = useState<string>('');
  const [sort, setSort] = useState<Sort>('last_updated');
  const [creators, setCreators] = useState<Record<string, boolean>>({});
  const [types, setTypes] = useState<
    Partial<Record<AssetSelectionEntities, boolean>>
  >({});
  const [assets, setAssets] = useState<PlaybookAsset[]>(playbook?.assets || []);
  const playbookId = useMemo(() => playbook?.id, [playbook]);
  const [multiTab, setMultiTab] = useState(
    playbook ? !!playbook.playSettings?.multiTab : playbookInitialMultiTab
  );
  const toggleMultiTab = () => {
    setMultiTab(!multiTab);
  };

  const {
    items,
    fetchFirstBatch,
    fetchNextBatch,
    isLoading,
    isLoadingBatch,
    error,
    isEmpty,
  } = useInfiniteScroll<PlaybookSearchResult>(useLazyGetSearchResultsQuery, {
    preferCacheValue: false,
  });

  const fetchArgs = useMemo((): SearchEndpointArgs => {
    const entities = Object.entries(types)
      .filter(([, bool]) => bool)
      .map(([type]) => type) as AssetSelectionEntities[];
    const creatorsArray = Object.entries(creators)
      .filter(([, bool]) => bool)
      .map(([creator]) => creator) as string[];
    return {
      includeShared: false,
      organizationId,
      query,
      workspaces: workspaceId,
      entities: entities.length ? entities : ['demo', 'tour'],
      creators: creatorsArray.length ? creatorsArray : undefined,
      sort,
    };
  }, [query, organizationId, types, creators, sort]);

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchArgs]);

  const isInitialState = useMemo(
    () =>
      initialState?.playbookName === playbookName &&
      initialState?.multiTab === multiTab &&
      isEqual(
        initialState?.assets.map(cleanAssetForComparison),
        assets.map(cleanAssetForComparison)
      ),
    [initialState, assets, playbookName, multiTab]
  );

  return {
    items,
    fetchNextBatch: () => {
      fetchNextBatch(fetchArgs);
    },
    isLoading,
    isLoadingBatch,
    error,
    query,
    setQuery,
    sort,
    setSort,
    creators,
    setCreators,
    types,
    setTypes,
    assets,
    setAssets,
    playbookName,
    setPlaybookName,
    workspaceId,
    playbookNameError,
    setPlaybookNameError,
    isEditingExternalLink,
    setIsEditingExternalLink,
    isEmpty,
    playbookId,
    isInitialState,
    multiTab,
    toggleMultiTab,
  };
};

export const [PlaybookModalProvider, usePlaybookModalState] = constate(
  usePlaybookModalContext
);
